import {t, useLanguage} from '../i18n';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  LOOP_BILLING_FEATURES,
  LOOP_BILLING_LIMITS,
  billingDateFromInput,
  billingDateInputValue,
  isLoopBillingAdminEmail,
  normalizeBusinessBilling,
} from "../utils/businessBilling";
import planLoopEarlyAccessCard from "../assets/plan-cards/plan-loop-early-access.png";
import planLoopBasicoCard from "../assets/plan-cards/plan-loop-basico.png";
import planLoopPremiumCard from "../assets/plan-cards/plan-loop-premium.png";
import planLoopBlackCard from "../assets/plan-cards/plan-loop-black.png";

const PLAN_OPTIONS = [
  // Mantengo los values existentes para no romper normalizeBusinessBilling/backend.
  // Solo cambiamos el nombre comercial que ve el negocio y el admin.
  { value: "free", label: "Plan Early Access" },
  { value: "pro", label: "Plan Básico" },
  { value: "premium", label: "Plan Premium" },
  { value: "enterprise", label: "Plan Black" },
];

const PLAN_CARD_ART = {
  early_access: { image: planLoopEarlyAccessCard, label: "Plan Loop Early Access" },
  basico: { image: planLoopBasicoCard, label: "Plan Loop Básico" },
  premium: { image: planLoopPremiumCard, label: "Plan Loop Premium" },
  black: { image: planLoopBlackCard, label: "Plan Loop Black" },
};

const EARLY_ACCESS_INCLUDED_FEATURES = {
  whatsappAutomation: true,
  loyalty: true,
  memberships: true,
  inventory: true,
};

const STATUS_OPTIONS = [
  { value: "trialing", label: "Early Access" },
  { value: "active", label: "Activo" },
  { value: "past_due", label: "Pago pendiente" },
  { value: "paused", label: "Pausado" },
  { value: "cancelled", label: "Cancelado" },
];

const CLIENT_BENEFITS = [
  {
    key: "onlineBooking",
    title: "Reservas online",
    copy: "Tus clientes pueden encontrar el negocio y reservar servicios desde Loop.",
    alwaysIncluded: true,
  },
  {
    key: "adminCalendar",
    title: "Calendario admin",
    copy: "Organiza reservas, horarios y disponibilidad del equipo en un solo lugar.",
    alwaysIncluded: true,
  },
  {
    key: "team",
    title: "Equipo y trabajadores",
    copy: "Administra perfiles, servicios, horarios y permisos por trabajador.",
    alwaysIncluded: true,
  },
  {
    key: "clients",
    title: "Clientes",
    copy: "Mantén una base de clientes conectada con reservas y actividad.",
    alwaysIncluded: true,
  },
  {
    key: "loyalty",
    title: "Fidelidad",
    copy: "Programas de puntos, sellos y beneficios para que tus clientes vuelvan.",
    featureKeys: ["loyalty", "loyaltyPrograms", "clientLoyalty"],
  },
  {
    key: "memberships",
    title: "Membresías",
    copy: "Paquetes, créditos y beneficios recurrentes para clientes frecuentes.",
    featureKeys: ["memberships", "membership", "clientMemberships"],
  },
  {
    key: "whatsappAutomation",
    title: "WhatsApp automático",
    copy: "Confirmaciones, cancelaciones y recordatorios enviados automáticamente.",
    featureKeys: ["whatsappAutomation", "whatsappAuto", "automatedWhatsapp"],
  },
  {
    key: "onlinePayments",
    title: "Pagos online",
    copy: "Adelantos, links de pago y cobros digitales conectados al flujo de reserva.",
    featureKeys: ["onlinePayments", "payments", "deposits", "paymentLinks"],
  },
  {
    key: "analytics",
    title: "Reportes",
    copy: "Visibilidad sobre reservas, rendimiento del negocio y operación diaria.",
    featureKeys: ["analytics", "reports", "businessAnalytics"],
    defaultIncluded: true,
  },
  {
    key: "inventory",
    title: "Inventario",
    copy: "Productos, stock y reglas de consumo conectadas a servicios y ventas.",
    featureKeys: ["inventory", "inventoryManagement", "stock"],
  },
];

function Toggle({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      className={`billing-switch ${checked ? "on" : ""}`}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      aria-label={label}
      aria-pressed={checked}
    >
      <span />
    </button>
  );
}

function Pill({ children, tone = "neutral" }) {
  return <span className={`billing-pill ${tone}`}>{children}</span>;
}

function billingLimitLabel(value) {
  const n = Number(value);
  if (n === -1) return "Ilimitado";
  if (!Number.isFinite(n)) return "0";
  return String(Math.max(0, Math.floor(n)));
}

function billingUsagePercent(usage = {}) {
  if (usage?.unlimited) return 0;
  const pct = Number(usage?.usagePercent || 0);
  return Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0;
}

function friendlyPlanName(plan, label) {
  const cleanLabel = String(label || "").trim();
  if (cleanLabel) {
    const normalized = cleanLabel.toLowerCase();
    if (normalized === "free / early access" || normalized === "early access") return "Plan gratis";
    if (normalized === "pro" || normalized === "loop pro") return "Plan Básico";
    if (normalized === "premium" || normalized === "loop premium") return "Plan Premium";
    if (normalized === "enterprise" || normalized === "loop enterprise") return "Plan Black";
    return cleanLabel;
  }

  const value = String(plan || "free").trim().toLowerCase();
  if (value === "free" || value === "early_access") return "Plan Early Access";
  if (value === "pro" || value === "basic" || value === "basico" || value === "básico") return "Plan Básico";
  if (value === "premium") return "Plan Premium";
  if (value === "enterprise" || value === "black") return "Plan Black";
  return "Plan Loop";
}

function friendlyStatusLabel(status) {
  const value = String(status || "trialing").trim().toLowerCase();
  if (value === "active") return "Activo";
  if (value === "trialing") return "Early Access";
  if (value === "past_due") return "En revisión";
  if (value === "paused") return "Pausado";
  if (value === "cancelled") return "Inactivo";
  return "Activo";
}

function statusTone(status) {
  const value = String(status || "").trim().toLowerCase();
  if (value === "active" || value === "trialing") return "success";
  if (value === "paused" || value === "past_due") return "warning";
  if (value === "cancelled") return "danger";
  return "neutral";
}

function formatBillingDate(value) {
  const input = billingDateInputValue(value);
  if (!input) return "";
  const [year, month, day] = String(input).split("-");
  if (!year || !month || !day) return input;
  return `${day}/${month}/${year}`;
}

function featureEnabled(features = {}, keys = [], fallback = false) {
  if (!Array.isArray(keys) || keys.length === 0) return Boolean(fallback);
  return keys.some((key) => features?.[key] === true) || Boolean(fallback);
}

function isEarlyAccessBilling(billing = {}) {
  const plan = String(billing?.plan || "").trim().toLowerCase();
  const status = String(billing?.status || "").trim().toLowerCase();
  return billing?.earlyAccess === true || plan === "free" || plan === "early_access" || status === "trialing";
}

function withEarlyAccessDefaults(billing = {}) {
  const normalized = billing || {};
  if (!isEarlyAccessBilling(normalized)) return normalized;
  return {
    ...normalized,
    features: {
      ...(normalized.features || {}),
      ...EARLY_ACCESS_INCLUDED_FEATURES,
    },
  };
}

function normalizeBillingPlanCardKey(value) {
  const raw = String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

  if (!raw) return "";
  if (raw.includes("early") || raw.includes("access") || raw.includes("trial") || raw.includes("beta") || raw === "free") return "early_access";
  if (raw.includes("black") || raw.includes("enterprise") || raw.includes("elite")) return "black";
  if (raw.includes("premium")) return "premium";
  if (raw.includes("basic") || raw.includes("basico") || raw.includes("pro") || raw.includes("starter")) return "basico";
  return "";
}

function getBillingPlanArt(billing = {}) {
  const candidates = [billing?.plan, billing?.planKey, billing?.tier, billing?.planLabel, billing?.status];
  for (const candidate of candidates) {
    const key = normalizeBillingPlanCardKey(candidate);
    if (key && PLAN_CARD_ART[key]) return PLAN_CARD_ART[key];
  }
  if (billing?.earlyAccess === true) return PLAN_CARD_ART.early_access;
  return PLAN_CARD_ART.early_access;
}

export default function BillingSection({ businessId, businessDoc = {}, auth, onBusinessPatch = () => {}, externalSaveRef = null, hideLocalActions = false }) {
  useLanguage();
  const currentEmail = String(auth?.currentUser?.email || "").trim().toLowerCase();
  const isAdmin = isLoopBillingAdminEmail(currentEmail);
  const [draft, setDraft] = useState(() => withEarlyAccessDefaults(normalizeBusinessBilling(businessDoc?.billing || {}, businessDoc || {})));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [adminOpen, setAdminOpen] = useState(false);
  const [whatsappUsage, setWhatsappUsage] = useState(null);
  const [whatsappUsageLoading, setWhatsappUsageLoading] = useState(false);
  const [whatsappUsageError, setWhatsappUsageError] = useState("");

  const loadWhatsAppUsage = useCallback(async () => {
    if (!businessId || !isAdmin) return;
    setWhatsappUsageLoading(true);
    setWhatsappUsageError("");
    try {
      const functions = getFunctions();
      const call = httpsCallable(functions, "getBusinessWhatsAppUsage");
      const res = await call({ businessId });
      setWhatsappUsage(res?.data || null);
    } catch (err) {
      console.warn("[BillingSection] WhatsApp usage failed", err);
      setWhatsappUsageError(err?.message || "No se pudo cargar consumo WhatsApp.");
    } finally {
      setWhatsappUsageLoading(false);
    }
  }, [businessId, isAdmin]);

  useEffect(() => {
    if (isAdmin && adminOpen) loadWhatsAppUsage();
  }, [isAdmin, adminOpen, loadWhatsAppUsage]);

  useEffect(() => {
    setDraft(withEarlyAccessDefaults(normalizeBusinessBilling(businessDoc?.billing || {}, businessDoc || {})));
  }, [businessDoc?.billing, businessDoc?.billingPlan, businessDoc?.billingStatus, businessDoc?.earlyAccess]);

  const groupedFeatures = useMemo(() => {
    const groups = {};
    Object.entries(LOOP_BILLING_FEATURES).forEach(([key, meta]) => {
      const group = meta.group || "General";
      if (!groups[group]) groups[group] = [];
      groups[group].push([key, meta]);
    });
    return groups;
  }, []);

  const clientBenefits = useMemo(() => {
    const features = draft.features || {};
    const earlyAccessActive = isEarlyAccessBilling(draft);
    return CLIENT_BENEFITS.map((benefit) => {
      const included = benefit.alwaysIncluded === true
        ? true
        : earlyAccessActive && EARLY_ACCESS_INCLUDED_FEATURES[benefit.key] === true
          ? true
          : featureEnabled(features, benefit.featureKeys, benefit.defaultIncluded === true);
      return { ...benefit, included };
    });
  }, [draft]);

  const earlyAccessActive = isEarlyAccessBilling(draft);
  const activeBenefitCount = clientBenefits.filter((benefit) => benefit.included).length;
  const availableBenefitCount = clientBenefits.length - activeBenefitCount;
  const activeBenefitPercent = clientBenefits.length > 0 ? Math.round((activeBenefitCount / clientBenefits.length) * 100) : 0;
  const internalFeatureCount = Object.values(draft.features || {}).filter(Boolean).length;
  const planName = friendlyPlanName(draft.plan, draft.planLabel);
  const planStatus = friendlyStatusLabel(draft.status);
  const planArt = getBillingPlanArt(draft);
  const earlyAccessEndsAtLabel = formatBillingDate(draft.earlyAccessEndsAtInput || draft.earlyAccessEndsAt);

  const includedBenefitGroups = useMemo(() => {
    const byKey = new Map(clientBenefits.map((benefit) => [benefit.key, benefit]));
    const groups = [
      {
        key: "bookings",
        title: "Reservas y agenda",
        copy: "Todo lo necesario para recibir y organizar reservas.",
        keys: ["onlineBooking", "adminCalendar"],
      },
      {
        key: "operations",
        title: "Operación diaria",
        copy: "Equipo, clientes, reportes e inventario en un solo lugar.",
        keys: ["team", "clients", "analytics", "inventory"],
      },
      {
        key: "growth",
        title: "Crecimiento",
        copy: "Herramientas para retener clientes y automatizar contacto.",
        keys: ["whatsappAutomation", "loyalty", "memberships"],
      },
    ];

    return groups
      .map((group) => ({
        ...group,
        items: group.keys.map((key) => byKey.get(key)).filter((benefit) => benefit && benefit.included),
      }))
      .filter((group) => group.items.length > 0);
  }, [clientBenefits]);

  const availableBenefits = useMemo(() => clientBenefits.filter((benefit) => !benefit.included), [clientBenefits]);

  const updateDraft = (patch) => {
    setStatus("");
    setDraft((prev) => withEarlyAccessDefaults(normalizeBusinessBilling({ ...prev, ...patch }, businessDoc || {})));
  };

  const setFeature = (key, value) => {
    setStatus("");
    setDraft((prev) => ({ ...prev, features: { ...(prev.features || {}), [key]: value === true } }));
  };

  const setLimit = (key, value) => {
    const n = Number(value);
    setStatus("");
    setDraft((prev) => ({ ...prev, limits: { ...(prev.limits || {}), [key]: Number.isFinite(n) ? n : 0 } }));
  };

  const save = useCallback(async () => {
    if (!businessId) return;
    if (!isAdmin) {
      const msg = "Esta vista es informativa. Los cambios de plan los gestiona Loop.";
      setStatus(msg);
      throw new Error(msg);
    }
    setSaving(true);
    setStatus("");
    try {
      const functions = getFunctions();
      const call = httpsCallable(functions, "setBusinessBillingAdmin");
      const selectedEarlyAccessEndsAt = Object.prototype.hasOwnProperty.call(draft, "earlyAccessEndsAtInput")
        ? billingDateFromInput(draft.earlyAccessEndsAtInput)
        : (draft.earlyAccessEndsAt || null);
      const draftToSave = withEarlyAccessDefaults(draft);
      const payload = {
        businessId,
        billing: {
          plan: draftToSave.plan,
          status: draftToSave.status,
          earlyAccess: isEarlyAccessBilling(draftToSave),
          earlyAccessUnlockAll: draftToSave.earlyAccessUnlockAll === true,
          earlyAccessEndsAt: selectedEarlyAccessEndsAt,
          billingProvider: draftToSave.billingProvider || "none",
          enforceFeatureGates: draftToSave.enforceFeatureGates === true,
          notes: draftToSave.notes || "",
          features: draftToSave.features || {},
          limits: draftToSave.limits || {},
        },
      };
      const result = await call(payload);
      const nextBilling = result?.data?.billing || payload.billing;
      onBusinessPatch({ billing: nextBilling, billingPlan: nextBilling.plan, billingStatus: nextBilling.status, earlyAccess: nextBilling.earlyAccess });
      setDraft(withEarlyAccessDefaults(normalizeBusinessBilling(nextBilling, { ...(businessDoc || {}), billing: nextBilling })));
      setStatus("Plan Loop y beneficios actualizados.");
      setTimeout(() => loadWhatsAppUsage(), 250);
      return { ok: true, message: "Plan Loop y beneficios actualizados." };
    } catch (err) {
      console.error("[BillingSection] save failed", err);
      setStatus(err?.message || "No se pudo guardar billing.");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [businessDoc, businessId, draft, isAdmin, onBusinessPatch, loadWhatsAppUsage]);

  const saveWhatsAppLimitOnly = useCallback(async () => {
    if (!businessId || !isAdmin) return;
    setSaving(true);
    setStatus("");
    try {
      const functions = getFunctions();
      const call = httpsCallable(functions, "updateBusinessWhatsAppBilling");
      const limit = Number(draft?.limits?.whatsappMessages ?? 0);
      const result = await call({
        businessId,
        whatsappMonthlyLimit: Number.isFinite(limit) ? limit : 0,
        whatsappManual: draft?.features?.whatsappManual === true,
        whatsappAutomation: draft?.features?.whatsappAutomation === true,
        notes: draft?.notes || "",
      });
      const nextBilling = result?.data?.billing || null;
      if (nextBilling) {
        onBusinessPatch({ billing: nextBilling, billingPlan: nextBilling.plan, billingStatus: nextBilling.status, earlyAccess: nextBilling.earlyAccess });
        setDraft(withEarlyAccessDefaults(normalizeBusinessBilling(nextBilling, { ...(businessDoc || {}), billing: nextBilling })));
      }
      if (result?.data?.usage) setWhatsappUsage(result.data.usage);
      else await loadWhatsAppUsage();
      setStatus("Límite WhatsApp actualizado.");
    } catch (err) {
      console.error("[BillingSection] saveWhatsAppLimitOnly failed", err);
      setStatus(err?.message || "No se pudo actualizar límite WhatsApp.");
    } finally {
      setSaving(false);
    }
  }, [businessDoc, businessId, draft, isAdmin, loadWhatsAppUsage, onBusinessPatch]);

  useEffect(() => {
    if (!externalSaveRef) return undefined;
    externalSaveRef.current = save;
    return () => {
      if (externalSaveRef.current === save) externalSaveRef.current = null;
    };
  }, [externalSaveRef, save]);

  return (
    <div className="billing-shell billing-client-friendly">
      <style>{styles}</style>

      <section className="billing-plan-card">
        <div className="billing-plan-copy">
          <span>{t("Plan Loop")}</span>
          <h2>{planName}</h2>
          <p>{t("Un resumen simple de lo que el negocio tiene activo en Loop, sin controles técnicos ni información interna.")}</p>
          <div className="billing-plan-pills">
            <Pill tone={statusTone(draft.status)}>{planStatus}</Pill>
            {earlyAccessActive ? <Pill tone="success">{t("Early Access")}</Pill> : null}
            <Pill tone="neutral">{activeBenefitCount}{" " + t("beneficios activos")}</Pill>
          </div>
        </div>

        <div className="billing-plan-side">
          {draft.plan === "free" ? <div className="billing-plan-art-image" style={{padding:32,background:"#154e45",color:"#fff",borderRadius:20}}><h2>{t("Plan gratis")}</h2><p>{t("Sin costo mensual")}</p></div> : <img className="billing-plan-art-image" src={planArt.image} alt={planArt.label} draggable={false} />}
          <div className="billing-plan-score">
            <small>{t("Herramientas activas")}</small>
            <strong>{activeBenefitCount}<em>/{clientBenefits.length}</em></strong>
            <div className="billing-plan-meter"><i style={{ width: `${activeBenefitPercent}%` }} /></div>
            <span>{availableBenefitCount > 0 ? `${availableBenefitCount} pendiente${availableBenefitCount === 1 ? "" : "s"} para más adelante` : "Todo incluido"}</span>
          </div>
        </div>
      </section>

      {earlyAccessActive ? (
        <section className="billing-soft-note">
          <strong>{t("Beneficios de lanzamiento activos")}</strong>
          <span>{earlyAccessEndsAtLabel ? `Disponibles hasta el ${earlyAccessEndsAtLabel}.` : "Este negocio tiene beneficios especiales de Early Access."}</span>
        </section>
      ) : null}

      <section className="billing-benefits-simple">
        <div className="billing-simple-head">
          <div>
            <span>{t("Incluido ahora")}</span>
            <h3>{t("Lo que el negocio puede usar")}</h3>
          </div>
          <p>{t("Organizado por áreas para que el dueño lo entienda rápido.")}</p>
        </div>

        <div className="billing-benefit-groups">
          {includedBenefitGroups.map((group) => (
            <article key={group.key} className="billing-benefit-group">
              <div className="billing-benefit-group-head">
                <strong>{t(group.title)}</strong>
                <span>{t(group.copy)}</span>
              </div>
              <div className="billing-benefit-list">
                {group.items.map((benefit) => (
                  <div key={benefit.key} className="billing-benefit-row">
                    <i>✓</i>
                    <span>{t(benefit.title)}</span>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>

        {availableBenefits.length > 0 ? (
          <div className="billing-next-benefits">
            <strong>{t("Disponible más adelante")}</strong>
            <div>
              {availableBenefits.map((benefit) => <span key={benefit.key}>{t(benefit.title)}</span>)}
            </div>
          </div>
        ) : null}
      </section>

      {isAdmin ? (
        <section className="billing-admin-entry">
          <div>
            <span>Loop Admin</span>
            <strong>{t("Controles internos ocultos")}</strong>
            <p>{t("Plan, early access, feature gates, límites, WhatsApp billing y notas internas.")}</p>
          </div>
          <button type="button" onClick={() => setAdminOpen((open) => !open)}>
            {adminOpen ? "Ocultar controles" : "Ver controles internos"}
          </button>
        </section>
      ) : null}

      {isAdmin && adminOpen ? (
        <div className="billing-admin-panel">
          <div className="billing-grid">
            <section className="billing-card">
              <div className="billing-card-head">
                <span>{t("Plan comercial")}</span>
                <h3>{t("Estado interno de la empresa")}</h3>
              </div>
              <div className="billing-fields-grid">
                <label>
                  <span>{t("Plan")}</span>
                  <select disabled={!isAdmin} value={draft.plan} onChange={(e) => updateDraft({ plan: e.target.value })}>
                    {PLAN_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{t(opt.label)}</option>)}
                  </select>
                </label>
                <label>
                  <span>Status</span>
                  <select disabled={!isAdmin} value={draft.status} onChange={(e) => updateDraft({ status: e.target.value })}>
                    {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{t(opt.label)}</option>)}
                  </select>
                </label>
                <label>
                  <span>{t("Fin de early access")}</span>
                  <input
                    disabled={!isAdmin}
                    type="date"
                    value={draft.earlyAccessEndsAtInput ?? billingDateInputValue(draft.earlyAccessEndsAt)}
                    onChange={(e) => setDraft((prev) => ({ ...prev, earlyAccessEndsAtInput: e.target.value }))}
                  />
                </label>
                <label>
                  <span>{t("Proveedor de cobro")}</span>
                  <select disabled={!isAdmin} value={draft.billingProvider || "none"} onChange={(e) => updateDraft({ billingProvider: e.target.value })}>
                    <option value="none">{t("Sin cobro todavía")}</option>
                    <option value="manual">Manual</option>
                    <option value="wompi">Wompi</option>
                    <option value="stripe">Stripe</option>
                  </select>
                </label>
              </div>

              <div className="billing-toggle-row">
                <div>
                  <strong>{t("Early access gratis")}</strong>
                  <span>{t("Marca negocios que reciben beneficios sin cobro durante la etapa inicial.")}</span>
                </div>
                <Toggle disabled={!isAdmin} checked={draft.earlyAccess} onChange={(v) => updateDraft({ earlyAccess: v, status: v ? "trialing" : draft.status })} label={t("Early access")} />
              </div>
              <div className="billing-toggle-row">
                <div>
                  <strong>{t("Desbloquear todo por early access")}</strong>
                  <span>{t("Útil para clientes piloto. Activa todos los features sin cobrar.")}</span>
                </div>
                <Toggle disabled={!isAdmin || !draft.earlyAccess} checked={draft.earlyAccessUnlockAll} onChange={(v) => updateDraft({ earlyAccessUnlockAll: v })} label={t("Desbloquear todo")} />
              </div>
              <div className="billing-toggle-row danger-soft">
                <div>
                  <strong>{t("Forzar feature gates")}</strong>
                  <span>{t("Déjalo apagado por ahora. Cuando esté activo, el backend podrá bloquear funciones no incluidas.")}</span>
                </div>
                <Toggle disabled={!isAdmin} checked={draft.enforceFeatureGates} onChange={(v) => updateDraft({ enforceFeatureGates: v })} label={t("Forzar feature gates")} />
              </div>
            </section>

            <section className="billing-card">
              <div className="billing-card-head">
                <span>{t("Resumen interno")}</span>
                <h3>{internalFeatureCount}{" " + t("feature gates activos")}</h3>
              </div>
              <div className="billing-summary-list">
                <div><strong>{t("WhatsApp automático")}</strong><span>{draft.features?.whatsappAutomation ? t("Activo") : "No incluido"}</span></div>
                <div><strong>{t("Pagos online")}</strong><span>{draft.features?.onlinePayments ? t("Activo") : "No incluido"}</span></div>
                <div><strong>{t("Lealtad")}</strong><span>{draft.features?.loyalty ? t("Activo") : "No incluido"}</span></div>
                <div><strong>{t("Membresías")}</strong><span>{draft.features?.memberships ? t("Activo") : "No incluido"}</span></div>
              </div>
              <label className="billing-notes">
                <span>{t("Notas internas")}</span>
                <textarea disabled={!isAdmin} value={draft.notes || ""} onChange={(e) => updateDraft({ notes: e.target.value })} placeholder={t("Ej: Cliente piloto de belleza, no cobrar hasta agosto...")} />
              </label>
            </section>
          </div>

          <section className="billing-card billing-wide-card billing-whatsapp-admin-card">
            <div className="billing-card-head compact">
              <div>
                <span>{t("WhatsApp billing")}</span>
                <h3>{t("Control interno de consumo")}</h3>
              </div>
              <p>{t("Administra el límite mensual real que usa el backend antes de enviar confirmaciones, cancelaciones, recordatorios o mensajes manuales.")}</p>
            </div>
            <div className="billing-wa-admin-grid">
              <div className="billing-wa-usage-box">
                <span>{t("Uso del mes") + " "}{whatsappUsage?.monthKey || ""}</span>
                <strong>{whatsappUsageLoading ? "..." : `${whatsappUsage?.sentCount ?? 0} / ${whatsappUsage?.unlimited ? "∞" : billingLimitLabel(whatsappUsage?.monthlyLimit ?? draft?.limits?.whatsappMessages)}`}</strong>
                <div className="billing-wa-progress"><i style={{ width: `${billingUsagePercent(whatsappUsage)}%` }} /></div>
                <small>{whatsappUsage?.unlimited ? "Negocio ilimitado" : `${whatsappUsage?.remaining ?? "—"} mensajes disponibles`}</small>
                {whatsappUsageError ? <em>{whatsappUsageError}</em> : null}
              </div>
              <div className="billing-wa-controls">
                <label>
                  <span>{t("Límite mensual WhatsApp")}</span>
                  <input disabled={!isAdmin} type="number" value={draft.limits?.whatsappMessages ?? 0} onChange={(e) => setLimit("whatsappMessages", e.target.value)} />
                  <em>{t("Usa -1 para ilimitado. Este valor se bloquea en backend cuando se alcanza el límite.")}</em>
                </label>
                <div className="billing-wa-quick-buttons">
                  {[50, 100, 300, 500, 1000, -1].map((value) => (
                    <button key={value} type="button" disabled={!isAdmin} onClick={() => setLimit("whatsappMessages", value)}>{value === -1 ? "Ilimitado" : value}</button>
                  ))}
                </div>
                <div className="billing-wa-actions">
                  <button type="button" disabled={!isAdmin || saving} onClick={saveWhatsAppLimitOnly}>{t("Guardar límite WhatsApp")}</button>
                  <button type="button" disabled={!isAdmin || whatsappUsageLoading} onClick={loadWhatsAppUsage}>{t("Actualizar consumo")}</button>
                </div>
              </div>
              <div className="billing-wa-breakdown">
                <div><strong>{whatsappUsage?.confirmationCount ?? 0}</strong><span>{t("Confirmaciones")}</span></div>
                <div><strong>{whatsappUsage?.cancellationCount ?? 0}</strong><span>{t("Cancelaciones")}</span></div>
                <div><strong>{whatsappUsage?.reminder24hCount ?? 0}</strong><span>{t("Record. 24h")}</span></div>
                <div><strong>{whatsappUsage?.reminder2hCount ?? 0}</strong><span>{t("Record. 2h")}</span></div>
                <div><strong>{whatsappUsage?.manualCount ?? 0}</strong><span>{t("Manuales")}</span></div>
                <div><strong>{whatsappUsage?.skippedCount ?? 0}</strong><span>{t("Saltados")}</span></div>
              </div>
            </div>
          </section>

          <section className="billing-card billing-wide-card">
            <div className="billing-card-head compact">
              <div>
                <span>{t("Feature gates")}</span>
                <h3>{t("Beneficios por módulo")}</h3>
              </div>
              <p>{t("Esto prepara WhatsApp, Wompi, lealtad y membresías para cobrarse después sin rehacer la arquitectura.")}</p>
            </div>
            <div className="billing-feature-groups">
              {Object.entries(groupedFeatures).map(([group, items]) => (
                <div className="billing-feature-group" key={group}>
                  <h4>{group}</h4>
                  {items.map(([key, meta]) => (
                    <div className="billing-feature-row" key={key}>
                      <div>
                        <strong>{t(meta.label)}</strong>
                        <span>{key}</span>
                      </div>
                      <Toggle disabled={!isAdmin || draft.earlyAccessUnlockAll} checked={draft.features?.[key] === true} onChange={(v) => setFeature(key, v)} label={t(meta.label)} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>

          <section className="billing-card billing-wide-card">
            <div className="billing-card-head compact">
              <div>
                <span>{t("Límites")}</span>
                <h3>{t("Uso incluido")}</h3>
              </div>
              <p>{t("Usa -1 para ilimitado. Todavía no bloqueamos fuerte, pero queda listo para consumo y pricing.")}</p>
            </div>
            <div className="billing-limits-grid">
              {Object.entries(LOOP_BILLING_LIMITS).map(([key, meta]) => (
                <label key={key}>
                  <span>{t(meta.label)}</span>
                  <input disabled={!isAdmin} type="number" value={draft.limits?.[key] ?? 0} onChange={(e) => setLimit(key, e.target.value)} />
                  <em>{meta.unit}</em>
                </label>
              ))}
            </div>
          </section>

          {!hideLocalActions ? (
            <div className="billing-actions">
              <span>{status}</span>
              <button type="button" disabled={!isAdmin || saving} onClick={() => save().catch(() => {})}>{saving ? t("Guardando...") : "Guardar Plan Loop"}</button>
            </div>
          ) : status ? (
            <div className="billing-inline-status">{status}</div>
          ) : null}
        </div>
      ) : status ? (
        <div className="billing-inline-status">{status}</div>
      ) : null}
    </div>
  );
}

const styles = `
.billing-shell{display:grid;gap:16px;color:#0f172a;}
.billing-plan-card{border:1px solid rgba(0,103,95,.14);background:radial-gradient(circle at 12% 15%,rgba(0,137,127,.10),transparent 32%),linear-gradient(135deg,#ffffff,#f5fffc);border-radius:30px;padding:24px;display:grid;grid-template-columns:minmax(0,1fr) minmax(300px,430px);gap:22px;align-items:stretch;box-shadow:0 20px 54px rgba(15,23,42,.07);}
.billing-plan-copy{display:flex;flex-direction:column;justify-content:center;min-height:150px;}.billing-plan-copy span,.billing-simple-head span,.billing-admin-entry span{display:block;color:#00675f;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.14em;margin-bottom:8px;}.billing-plan-copy h2{margin:0;color:#123f39;font-size:40px;line-height:.98;font-weight:950;letter-spacing:-.07em;}.billing-plan-copy p{margin:12px 0 0;max-width:680px;color:#64748b;font-size:14px;line-height:1.5;font-weight:750;}.billing-plan-pills{display:flex;gap:8px;flex-wrap:wrap;margin-top:18px;}
.billing-pill{display:inline-flex;align-items:center;min-height:28px;padding:0 11px;border-radius:999px;background:#f1f5f9;color:#475569;font-size:11px;font-weight:950;letter-spacing:.02em;}.billing-pill.success{background:#dcfce7;color:#047857}.billing-pill.warning{background:#fff7ed;color:#c2410c}.billing-pill.danger{background:#fee2e2;color:#b91c1c}
.billing-plan-side{display:grid;grid-template-rows:auto 1fr;gap:10px;align-items:stretch;min-width:0;}.billing-plan-art-image{display:block;width:100%;height:auto;aspect-ratio:auto;object-fit:contain;background:transparent;border:0;border-radius:0;filter:drop-shadow(0 14px 30px rgba(15,23,42,.09));image-rendering:auto;transform:translateZ(0);backface-visibility:hidden;user-select:none;pointer-events:none;}
.billing-plan-score{border:1px solid rgba(0,103,95,.12);background:rgba(255,255,255,.88);border-radius:24px;padding:18px;display:flex;flex-direction:column;justify-content:center;box-shadow:0 16px 36px rgba(0,103,95,.07);}.billing-plan-score small{color:#64748b;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.12em;}.billing-plan-score strong{display:block;margin-top:8px;color:#0f172a;font-size:44px;line-height:1;font-weight:950;letter-spacing:-.07em;}.billing-plan-score strong em{font-style:normal;color:#94a3b8;font-size:22px;letter-spacing:-.03em;}.billing-plan-score span{display:block;margin-top:10px;color:#64748b;font-size:12px;font-weight:800;}.billing-plan-meter{margin-top:14px;height:9px;border-radius:999px;background:#e2e8f0;overflow:hidden;}.billing-plan-meter i{display:block;height:100%;border-radius:999px;background:#00675f;min-width:12px;}
.billing-soft-note{border:1px solid #ccfbf1;background:#f0fdfa;border-radius:20px;padding:14px 16px;display:flex;align-items:center;justify-content:space-between;gap:14px;}.billing-soft-note strong{color:#123f39;font-size:14px;font-weight:950;}.billing-soft-note span{color:#64748b;font-size:13px;font-weight:800;line-height:1.35;}
.billing-benefits-simple{border:1px solid #e2e8f0;background:rgba(255,255,255,.94);border-radius:28px;padding:20px;box-shadow:0 16px 40px rgba(15,23,42,.055);}.billing-simple-head{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;margin-bottom:16px;}.billing-simple-head h3{margin:0;color:#0f172a;font-size:22px;font-weight:950;letter-spacing:-.05em;}.billing-simple-head p{margin:0;max-width:360px;color:#64748b;font-size:13px;line-height:1.45;font-weight:750;text-align:right;}
.billing-benefit-groups{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;}.billing-benefit-group{border:1px solid #e2e8f0;background:linear-gradient(135deg,#ffffff,#fbfefd);border-radius:22px;padding:15px;min-height:200px;}.billing-benefit-group-head strong{display:block;color:#0f172a;font-size:15px;font-weight:950;letter-spacing:-.03em;}.billing-benefit-group-head span{display:block;margin-top:5px;color:#64748b;font-size:12px;line-height:1.35;font-weight:750;}.billing-benefit-list{display:grid;gap:8px;margin-top:14px;}.billing-benefit-row{display:flex;align-items:center;gap:10px;border:1px solid #ccfbf1;background:#f0fdfa;border-radius:14px;min-height:42px;padding:0 11px;}.billing-benefit-row i{width:22px;height:22px;border-radius:999px;background:#dcfce7;color:#047857;display:flex;align-items:center;justify-content:center;font-style:normal;font-size:13px;font-weight:950;flex:0 0 auto;}.billing-benefit-row span{color:#0f172a;font-size:13px;font-weight:900;}
.billing-next-benefits{margin-top:14px;border:1px dashed #dbe5ee;background:#f8fafc;border-radius:18px;padding:13px 14px;display:flex;align-items:center;justify-content:space-between;gap:12px;}.billing-next-benefits strong{color:#475569;font-size:12px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}.billing-next-benefits div{display:flex;gap:7px;flex-wrap:wrap;justify-content:flex-end;}.billing-next-benefits span{display:inline-flex;align-items:center;min-height:26px;padding:0 10px;border-radius:999px;background:#fff;color:#64748b;border:1px solid #e2e8f0;font-size:11px;font-weight:900;}
.billing-card{border:1px solid #e2e8f0;background:rgba(255,255,255,.92);border-radius:24px;padding:18px;box-shadow:0 12px 30px rgba(15,23,42,.045);}.billing-card-head span{display:block;color:#00675f;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.14em;margin-bottom:8px;}.billing-card-head h3{margin:0;color:#0f172a;font-size:20px;font-weight:950;letter-spacing:-.04em;}.billing-card-head.compact{display:flex;align-items:flex-start;justify-content:space-between;gap:16px}.billing-card-head.compact p{margin:0;color:#64748b;font-size:13px;font-weight:700;max-width:450px;line-height:1.45;}
.billing-admin-entry{border:1px dashed #dbe5ee;background:rgba(255,255,255,.76);border-radius:22px;padding:14px 16px;display:flex;align-items:center;justify-content:space-between;gap:14px;}.billing-admin-entry strong{display:block;color:#0f172a;font-size:14px;font-weight:950}.billing-admin-entry p{margin:3px 0 0;color:#64748b;font-size:12px;font-weight:750;line-height:1.35}.billing-admin-entry button{border:1px solid #dbe5ee;background:#fff;color:#0f172a;border-radius:999px;min-height:38px;padding:0 14px;font-size:12px;font-weight:950;cursor:pointer;box-shadow:0 8px 18px rgba(15,23,42,.05);}
.billing-admin-panel{display:grid;gap:14px;border:1px solid rgba(0,103,95,.12);background:linear-gradient(135deg,rgba(240,253,250,.45),rgba(255,255,255,.72));border-radius:28px;padding:14px;}.billing-grid{display:grid;grid-template-columns:minmax(0,1.25fr) minmax(270px,.75fr);gap:14px;}.billing-fields-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:16px;}.billing-fields-grid label,.billing-limits-grid label,.billing-notes{display:grid;gap:7px;}.billing-fields-grid label span,.billing-limits-grid label span,.billing-notes span{font-size:12px;color:#475569;font-weight:900;}.billing-fields-grid select,.billing-fields-grid input,.billing-limits-grid input,.billing-notes textarea{width:100%;border:1px solid #dbe5ee;background:#fff;border-radius:14px;min-height:42px;padding:0 12px;color:#0f172a;font-size:13px;font-weight:800;outline:none;}.billing-notes{margin-top:14px}.billing-notes textarea{padding:12px;min-height:108px;resize:vertical;}.billing-toggle-row{margin-top:12px;border:1px solid #e2e8f0;border-radius:18px;padding:12px;display:flex;align-items:center;justify-content:space-between;gap:14px;background:#f8fafc;}.billing-toggle-row.danger-soft{background:#fff7ed;border-color:#fed7aa;}.billing-toggle-row strong{display:block;font-size:13px;font-weight:950;color:#0f172a}.billing-toggle-row span{display:block;margin-top:3px;font-size:12px;color:#64748b;font-weight:700;line-height:1.35;}.billing-switch{width:48px;height:28px;border-radius:999px;border:0;background:#cbd5e1;padding:3px;cursor:pointer;transition:.15s ease;flex:0 0 auto;}.billing-switch span{display:block;width:22px;height:22px;border-radius:50%;background:#fff;box-shadow:0 2px 8px rgba(15,23,42,.18);transition:.15s ease;}.billing-switch.on{background:#00675f}.billing-switch.on span{transform:translateX(20px)}.billing-switch:disabled{opacity:.58;cursor:not-allowed;}.billing-summary-list{display:grid;gap:8px;margin-top:16px}.billing-summary-list div{display:flex;align-items:center;justify-content:space-between;gap:10px;border-bottom:1px solid #eef2f7;padding-bottom:8px}.billing-summary-list strong{font-size:13px;font-weight:900}.billing-summary-list span{font-size:12px;color:#64748b;font-weight:850;}.billing-wide-card{display:grid;gap:16px;}.billing-feature-groups{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;}.billing-feature-group{border:1px solid #e2e8f0;border-radius:18px;padding:12px;background:#fbfdff;}.billing-feature-group h4{margin:0 0 10px;color:#123f39;font-size:13px;font-weight:950;}.billing-feature-row{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:9px 0;border-top:1px solid #edf2f7;}.billing-feature-row strong{display:block;font-size:12px;font-weight:900}.billing-feature-row span{display:block;margin-top:2px;font-size:10px;color:#94a3b8;font-weight:850;}.billing-limits-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;}.billing-limits-grid em{font-style:normal;color:#94a3b8;font-size:11px;font-weight:800;}.billing-wa-admin-grid{display:grid;grid-template-columns:1fr 1.2fr;gap:14px;align-items:stretch}.billing-wa-usage-box{border:1px solid #ccfbf1;background:linear-gradient(135deg,#f0fdfa,#fff);border-radius:20px;padding:16px;display:grid;gap:8px}.billing-wa-usage-box span,.billing-wa-controls label span{font-size:12px;color:#00675f;font-weight:950;text-transform:uppercase;letter-spacing:.08em}.billing-wa-usage-box strong{font-size:30px;font-weight:950;color:#0f172a;letter-spacing:-.05em}.billing-wa-usage-box small{font-size:12px;color:#475569;font-weight:850}.billing-wa-usage-box em{font-style:normal;color:#b91c1c;font-size:12px;font-weight:850}.billing-wa-progress{height:10px;border-radius:999px;background:#e2e8f0;overflow:hidden}.billing-wa-progress i{display:block;height:100%;border-radius:999px;background:#00675f;min-width:0}.billing-wa-controls{display:grid;gap:10px}.billing-wa-controls label{display:grid;gap:7px}.billing-wa-controls input{width:100%;border:1px solid #dbe5ee;background:#fff;border-radius:14px;min-height:44px;padding:0 12px;color:#0f172a;font-size:14px;font-weight:900;outline:none}.billing-wa-controls em{font-style:normal;color:#64748b;font-size:12px;font-weight:800}.billing-wa-quick-buttons{display:flex;flex-wrap:wrap;gap:7px}.billing-wa-quick-buttons button,.billing-wa-actions button{border:1px solid #dbe5ee;background:#fff;border-radius:999px;min-height:34px;padding:0 12px;color:#0f172a;font-size:12px;font-weight:950;cursor:pointer}.billing-wa-actions{display:flex;gap:8px;flex-wrap:wrap}.billing-wa-actions button:first-child{background:#00675f;color:white;border-color:#00675f}.billing-wa-breakdown{grid-column:1/-1;display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:8px}.billing-wa-breakdown div{border:1px solid #e2e8f0;background:#f8fafc;border-radius:16px;padding:12px}.billing-wa-breakdown strong{display:block;font-size:18px;font-weight:950;color:#0f172a}.billing-wa-breakdown span{display:block;margin-top:2px;color:#64748b;font-size:11px;font-weight:850}.billing-inline-status{border:1px solid #dbeafe;background:#eff6ff;color:#1e3a8a;border-radius:16px;padding:12px 14px;font-size:13px;font-weight:850;}.billing-actions{position:sticky;bottom:14px;z-index:10;border:1px solid #dbeafe;background:rgba(255,255,255,.96);backdrop-filter:blur(14px);border-radius:20px;padding:10px;display:flex;justify-content:space-between;align-items:center;gap:12px;box-shadow:0 18px 44px rgba(15,23,42,.12);}.billing-actions span{color:#475569;font-size:13px;font-weight:800;}.billing-actions button{border:0;background:linear-gradient(135deg,#004f47,#007467);color:#fff;border-radius:14px;min-height:42px;padding:0 18px;font-weight:950;cursor:pointer;}.billing-actions button:disabled{opacity:.55;cursor:not-allowed;}
@media(max-width:1050px){.billing-plan-card{grid-template-columns:1fr}.billing-plan-side{max-width:520px}.billing-benefit-groups{grid-template-columns:1fr}.billing-simple-head,.billing-soft-note,.billing-next-benefits{display:grid;text-align:left}.billing-simple-head p{text-align:left;max-width:none}.billing-plan-copy h2{font-size:34px}.billing-grid,.billing-wa-admin-grid{grid-template-columns:1fr}.billing-fields-grid,.billing-feature-groups,.billing-limits-grid,.billing-wa-breakdown{grid-template-columns:1fr}.billing-admin-entry{display:grid}.billing-actions{bottom:calc(var(--loop-safe-bottom,0px) + 84px)}}
`;
